import type React from 'react';
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HelpSupportButton } from '@/components/help-support';
import { ReceiptFilterSheet } from '@/components/payments/receipt-filter-sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  Target,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  CheckCircle2,
  ArrowRightCircle,
  ArrowDownToLine,
  MoreVertical,
  Pin,
  PinOff,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Settings,
  Info,
  Filter,
  Save,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { format } from 'date-fns';
import { clipCell } from '@/lib/table-cell-clip';
import { cn, formatDateTime } from '@/lib/utils';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useReceiptsPageDataGraphQL, sortFieldMap, buildGraphQLFilterVariable } from '@/hooks/useReceiptsGraphQL';
import type { GetReceiptsParams } from '@/hooks/useReceiptsGraphQL';
import type { AllocationStatus, Receipt, ReceiptFilters } from '@/types/payments';
import type { SavedFilterPreset } from '@/types/transaction';
import type { ColumnInput } from '@/types/export';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { useSessionSortPreference } from '@/hooks/useSessionSortPreference';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useExportAll } from '@/hooks/useExportAll';
import { ExportProvider } from '@/providers/ExportProvider';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { getPageNumbers } from '@/lib/pagination-utils';
import { useCompany } from '@/contexts/CompanyContext';
import { getCurrencyIcon } from '@/components/shared/currency-icon';
import { filtersMatch, useFilterPreferences } from '@/hooks/useFilterPreferences';

// ─── Local KPI Card ───────────────────────────────────────────────────────────

interface ReceiptsKPICardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: LucideIcon;
  colorScheme: 'blue' | 'green' | 'amber' | 'red';
  onClick?: () => void;
  isActive?: boolean;
  infoTooltip?: string;
  secondaryLabel?: string;
  secondaryValue?: string;
}

const receiptKpiColors: Record<
  ReceiptsKPICardProps['colorScheme'],
  { border: string; iconBg: string; iconColor: string }
> = {
  blue: { border: 'border-l-blue-500', iconBg: '#dbeafe', iconColor: '#2563eb' },
  green: { border: 'border-l-green-500', iconBg: '#dcfce7', iconColor: '#16a34a' },
  amber: { border: 'border-l-amber-500', iconBg: '#fef3c7', iconColor: '#d97706' },
  red: { border: 'border-l-red-500', iconBg: '#fee2e2', iconColor: '#dc2626' },
};

function ReceiptsKPICard({
  title,
  value,
  subtitle,
  icon: Icon,
  colorScheme,
  onClick,
  isActive,
  infoTooltip,
  secondaryLabel,
  secondaryValue,
}: ReceiptsKPICardProps) {
  const colors = receiptKpiColors[colorScheme];
  return (
    <div
      onClick={onClick}
      className={cn(
        'relative rounded-lg border bg-white shadow-sm border-l-4 p-4 cursor-pointer transition-all hover:shadow-md flex flex-col h-full',
        colors.border,
        isActive && 'ring-2 ring-offset-1 ring-gray-300',
      )}
    >
      <div className="absolute top-3 right-3 p-2 rounded-full" style={{ backgroundColor: colors.iconBg }}>
        <Icon className="h-4 w-4" style={{ color: colors.iconColor }} />
      </div>
      <div className="pr-12 flex flex-col flex-1">
        <div>
          <div className="flex items-center gap-1">
            <p style={{ fontSize: 15, fontWeight: 400, color: '#111827' }}>{title}</p>
            {infoTooltip && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info
                      className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0"
                      aria-label={`${title} info`}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[220px] text-center">
                    {infoTooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <p style={{ fontSize: 22, fontWeight: 600, color: '#111827' }} className="mt-1">
            {value}
          </p>
          {secondaryLabel && secondaryValue && (
            <p style={{ fontSize: 12, fontWeight: 400, color: '#6b7280' }} className="mt-1">
              {secondaryLabel} {secondaryValue}
            </p>
          )}
        </div>
        <p style={{ fontSize: 12, fontWeight: 400, color: '#6b7280' }} className="mt-auto">
          {subtitle}
        </p>
      </div>
    </div>
  );
}

// ─── Column Definitions ───────────────────────────────────────────────────────

const DEFAULT_RECEIPT_COLUMNS: ColumnDefinition[] = [
  { id: 'receiptNumber', label: 'Receipt #', visible: true, order: 0 },
  { id: 'customerAccountNumber', label: 'Account Number', visible: true, order: 1 },
  { id: 'customerName', label: 'Customer', visible: true, order: 2 },
  { id: 'currency', label: 'Currency', visible: true, order: 3 },
  { id: 'receiptDate', label: 'Receipt Date', visible: true, order: 4 },
  { id: 'receivedAmount', label: 'Gross Amount', visible: true, order: 5 },
  { id: 'allocatedAmount', label: 'Allocated Amount', visible: false, order: 6 },
  { id: 'unappliedAmount', label: 'Unallocated Amount', visible: true, order: 7 },
  { id: 'allocationStatus', label: 'Status', visible: true, order: 8 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 9 },
  { id: 'createdAt', label: 'Created At', visible: false, order: 10 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 11 },
  { id: 'modifiedAt', label: 'Modified At', visible: false, order: 12 },
  { id: 'actions', label: 'Actions', visible: true, order: 13 },
];

const COL_CONFIG: Record<string, { label: string; sortField?: string; align?: 'right' }> = {
  receiptNumber: { label: 'Receipt #', sortField: 'receiptNumber' },
  customerAccountNumber: { label: 'Account Number', sortField: 'customerAccountNumber' },
  customerName: { label: 'Customer', sortField: 'customerName' },
  currency: { label: 'Currency' },
  receiptDate: { label: 'Receipt Date', sortField: 'receiptDate' },
  receivedAmount: { label: 'Gross Amount', sortField: 'receivedAmount', align: 'right' },
  allocatedAmount: { label: 'Allocated Amount', align: 'right' },
  unappliedAmount: { label: 'Unallocated Amount', sortField: 'unappliedAmount', align: 'right' },
  allocationStatus: { label: 'Status', sortField: 'allocationStatus' },
  createdBy: { label: 'Created By' },
  createdAt: { label: 'Created At' },
  modifiedBy: { label: 'Modified By' },
  modifiedAt: { label: 'Modified At' },
  actions: { label: 'Actions', align: 'right' },
};

type SortField =
  | 'receiptNumber'
  | 'customerAccountNumber'
  | 'customerName'
  | 'receiptDate'
  | 'receivedAmount'
  | 'unappliedAmount'
  | 'allocationStatus';
type SortDirection = 'asc' | 'desc' | null;

const DEFAULT_UNALLOCATED_RECEIPTS_PRESET_ID = '__default_unallocated_receipts__';

const UNALLOCATED_RECEIPTS_PRESET: SavedFilterPreset<ReceiptFilters> = {
  id: DEFAULT_UNALLOCATED_RECEIPTS_PRESET_ID,
  name: 'Receipts Requiring Allocation',
  filters: {
    customerIds: [],
    creditControllerIds: [],
    allocationStatuses: [],
    currencies: [],
    unappliedNotZero: true,
  },
  isDefault: true,
};

function normalizeReceiptFilters(filters: ReceiptFilters): ReceiptFilters {
  return {
    ...filters,
    customerIds: filters.customerIds ?? [],
    creditControllerIds: filters.creditControllerIds ?? [],
    allocationStatuses: filters.allocationStatuses ?? [],
    currencies: filters.currencies ?? [],
  };
}

function getInitialReceiptFilters(searchParams: URLSearchParams): ReceiptFilters {
  const creditControllerId = searchParams.get('creditControllerId');

  if (searchParams.get('filter') === 'unallocated') {
    return {
      ...UNALLOCATED_RECEIPTS_PRESET.filters,
      creditControllerIds: creditControllerId?.split(',').filter(Boolean) ?? [],
      allocationStatuses: ['unallocated'],
      unappliedNotZero: undefined,
    };
  }

  return { ...UNALLOCATED_RECEIPTS_PRESET.filters };
}

function joinFilterValues(values: readonly (string | number)[]): string | undefined {
  return values.length > 0 ? values.join(',') : undefined;
}

export default function ReceiptsPage() {
  return (
    <ExportProvider>
      <ReceiptsPageInner />
    </ExportProvider>
  );
}

function ReceiptsPageInner() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const baseCurrency = selectedCompany?.currency || 'GBP';
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '');
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [filters, setFilters] = useState<ReceiptFilters>(() => getInitialReceiptFilters(searchParams));
  const [metricAllocationStatus, setMetricAllocationStatus] = useState<string | null>(null);
  const { savedPresets, activePresetId, savePreset, deletePreset, applyPreset, clearActivePreset, setActivePresetId } =
    useFilterPreferences<ReceiptFilters>(UniqueTableNamesForColumnConfiguration.Receipts, {
      defaultPresets: [UNALLOCATED_RECEIPTS_PRESET],
    });
  const normalizedSavedPresets = useMemo(
    () => savedPresets.map((x) => ({ ...x, filters: normalizeReceiptFilters(x.filters) })),
    [savedPresets],
  );
  const clearNavigationFilterContext = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('filter');
        next.delete('creditControllerId');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const effectiveActivePresetId = useMemo(() => {
    if (!activePresetId) return null;
    const activePreset = normalizedSavedPresets.find((x) => x.id === activePresetId);
    if (!activePreset) return null;
    return filtersMatch(activePreset.filters, filters) ? activePresetId : null;
  }, [activePresetId, normalizedSavedPresets, filters]);

  const handleKpiClick = (allocationStatus: string) => {
    clearNavigationFilterContext();
    const isActive = filters.allocationStatuses.length === 1 && filters.allocationStatuses[0] === allocationStatus;
    setMetricAllocationStatus(isActive ? null : allocationStatus);
    setFilters((prev) => {
      return {
        ...prev,
        allocationStatuses: isActive ? [] : [allocationStatus],
        unappliedNotZero: isActive ? true : undefined,
      };
    });
  };

  // Sorting (persisted per screen for the user session)
  const [{ column: sortField, direction: sortDirection }, setSort] = useSessionSortPreference<
    SortField | null,
    SortDirection
  >(UniqueTableNamesForColumnConfiguration.Receipts, null, null);

  // Column interactions (persisted)
  const {
    columns: receiptColumns,
    visibleColumns: receiptVisibleColumns,
    savePreferences: saveReceiptColumnPreferences,
    resetToDefaults: resetReceiptColumnPreferences,
    columnOrder: persistedColumnOrder,
    setColumnOrder,
    columnWidths,
    setColumnWidths,
    pinnedLeft,
    pinnedRight,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.Receipts, DEFAULT_RECEIPT_COLUMNS);
  const columnOrder = persistedColumnOrder.length > 0 ? persistedColumnOrder : DEFAULT_RECEIPT_COLUMNS.map((c) => c.id);

  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);
  const dragColRef = useRef<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const pendingDraftFiltersRef = useRef<ReceiptFilters | null>(null);
  const isMetricAllocationStatusActive =
    metricAllocationStatus !== null &&
    filters.allocationStatuses.length === 1 &&
    filters.allocationStatuses[0] === metricAllocationStatus;
  const columnVisibility = useMemo(() => {
    const vis: Record<string, boolean> = {};
    receiptColumns.forEach((col) => {
      vis[col.id] = col.visible;
    });
    return vis;
  }, [receiptColumns]);

  // Build GraphQL params from current filter/sort/page state
  const graphqlParams = useMemo(
    (): GetReceiptsParams => ({
      page: currentPage,
      pageSize,
      sortBy: sortField ?? undefined,
      sortOrder: sortDirection ?? undefined,
      search: debouncedSearch || undefined,
      customerId: joinFilterValues(filters.customerIds),
      creditControllerId: joinFilterValues(filters.creditControllerIds),
      allocationStatus: isMetricAllocationStatusActive ? undefined : joinFilterValues(filters.allocationStatuses),
      metricAllocationStatus: isMetricAllocationStatusActive ? metricAllocationStatus : undefined,
      currency: joinFilterValues(filters.currencies),
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      amountMin: filters.amountMin,
      amountMax: filters.amountMax,
      outstandingMin: filters.unallocatedMin,
      outstandingMax: filters.unallocatedMax,
      outstandingNotZero: filters.unappliedNotZero || undefined,
      hasPromise: filters.hasPromise || undefined,
      onDispute: filters.onDisputeFilter || undefined,
      hasDispute: filters.hasDispute || undefined,
    }),
    [
      currentPage,
      pageSize,
      debouncedSearch,
      sortField,
      sortDirection,
      filters,
      metricAllocationStatus,
      isMetricAllocationStatusActive,
    ],
  );

  // Fetch receipts data using GraphQL (server-side filtering, sorting, pagination)
  const {
    receipts,
    pagination: graphqlPagination,
    totals,
    isLoading,
  } = useReceiptsPageDataGraphQL(graphqlParams, baseCurrency);

  // Server-side pagination from GraphQL
  const totalRecords = graphqlPagination.totalItems;
  const totalPages = graphqlPagination.totalPages;

  // When exactly one currency is selected in the filter, KPI cards should show totals in that currency
  const activeCurrencyFilter =
    filters.currencies && filters.currencies.length === 1 ? filters.currencies[0] : baseCurrency;
  const isSingleCurrencyFiltered = activeCurrencyFilter !== baseCurrency;

  // KPIs from server-side totals
  const kpis = useMemo(() => {
    const totalReceived = isSingleCurrencyFiltered ? totals.totalCurrencyAmount : totals.totalAmount;
    const fullyApplied = isSingleCurrencyFiltered ? totals.fullyAllocatedCurrencyAmount : totals.fullyAllocatedAmount;
    const partiallyApplied = isSingleCurrencyFiltered
      ? totals.partiallyAllocatedCurrencyAmount
      : totals.partiallyAllocatedAmount;
    const partiallyOutstanding = isSingleCurrencyFiltered
      ? totals.partiallyAllocatedOutstandingCurrencyAmount
      : totals.partiallyAllocatedOutstandingAmount;
    const unallocated = isSingleCurrencyFiltered ? totals.unallocatedCurrencyAmount : totals.unallocatedAmount;
    return {
      totalReceipts: totals.totalCount,
      totalReceived,
      fullyApplied,
      fullyAppliedCount: totals.fullyAllocatedCount,
      partiallyApplied,
      partiallyAppliedCount: totals.partiallyAllocatedCount,
      partiallyOutstanding,
      unapplied: unallocated - partiallyOutstanding,
      unappliedCount: totals.unallocatedCount - totals.partiallyAllocatedCount,
    };
  }, [totals, isSingleCurrencyFiltered]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortField, sortDirection, filters]);

  useEffect(() => {
    if (activePresetId && effectiveActivePresetId === null) {
      clearActivePreset();
    }
  }, [activePresetId, effectiveActivePresetId, clearActivePreset]);

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Server-side sorted/filtered receipts from GraphQL (already paginated)
  const sortedReceipts = receipts;

  const hasActiveFilters = !!(
    searchQuery ||
    filters.customerIds.length > 0 ||
    filters.creditControllerIds.length > 0 ||
    filters.allocationStatuses.length > 0 ||
    filters.currencies.length > 0 ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.amountMin !== undefined ||
    filters.amountMax !== undefined ||
    filters.unallocatedMin !== undefined ||
    filters.unallocatedMax !== undefined ||
    filters.hasPromise ||
    filters.onDisputeFilter ||
    filters.hasDispute
  );

  const activeFilterCount =
    (searchQuery ? 1 : 0) +
    (filters.customerIds.length > 0 ? 1 : 0) +
    (filters.creditControllerIds.length > 0 ? 1 : 0) +
    (filters.allocationStatuses.length > 0 ? 1 : 0) +
    (filters.currencies.length > 0 ? 1 : 0) +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0) +
    (filters.amountMin !== undefined || filters.amountMax !== undefined ? 1 : 0) +
    (filters.unallocatedMin !== undefined || filters.unallocatedMax !== undefined ? 1 : 0) +
    (filters.hasPromise ? 1 : 0) +
    (filters.onDisputeFilter ? 1 : 0) +
    (filters.hasDispute ? 1 : 0);

  const hasDefaultFilter = filters.unappliedNotZero === true;
  const isFilterActive = hasActiveFilters || hasDefaultFilter;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSort(field, 'desc');
      } else {
        setSort(null, null);
      }
    } else {
      setSort(field, 'asc');
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field)
      return (
        <span className="text-gray-500">
          <ArrowUpDown className="h-4 w-4" strokeWidth={2.5} />
        </span>
      );
    if (sortDirection === 'asc')
      return (
        <span className="text-gray-500">
          <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
        </span>
      );
    return (
      <span className="text-gray-500">
        <ArrowDown className="h-4 w-4" strokeWidth={2.5} />
      </span>
    );
  };

  const handleClearFilters = () => {
    clearNavigationFilterContext();
    setSearchQuery('');
    setFilters({ ...UNALLOCATED_RECEIPTS_PRESET.filters });
    setMetricAllocationStatus(null);
    setActivePresetId(DEFAULT_UNALLOCATED_RECEIPTS_PRESET_ID);
    setCurrentPage(1);
    toast.success('Filters reset to Receipts Requiring Allocation');
  };

  const handleApplyPreset = (preset: SavedFilterPreset<ReceiptFilters>) => {
    const normalizedPreset = { ...preset, filters: normalizeReceiptFilters(preset.filters) };
    clearNavigationFilterContext();
    setFilters({ ...normalizedPreset.filters });
    setMetricAllocationStatus(null);
    applyPreset(normalizedPreset);
    setCurrentPage(1);
    toast.success(`Filter preset "${preset.name}" applied`);
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Preset name required');
      return;
    }

    const filtersToSave = pendingDraftFiltersRef.current ?? filters;
    try {
      const newPresetId = await savePreset(presetName, filtersToSave);
      clearNavigationFilterContext();
      setFilters(normalizeReceiptFilters(filtersToSave));
      setMetricAllocationStatus((current) =>
        current && filtersToSave.allocationStatuses.length === 1 && filtersToSave.allocationStatuses[0] === current
          ? current
          : null,
      );
      setActivePresetId(newPresetId);
      setCurrentPage(1);
      setPresetName('');
      setSavePresetDialogOpen(false);
      pendingDraftFiltersRef.current = null;
      toast.success(`Filter preset "${presetName}" saved`);
    } catch (error) {
      console.error('Failed to save filter preset:', error);
      const message = error instanceof Error ? error.message : 'Failed to save filter preset';
      toast.error(message);
    }
  };

  const handleDeletePreset = async (presetId: string) => {
    const preset = savedPresets.find((x) => x.id === presetId);
    if (preset?.isDefault) return;

    try {
      await deletePreset(presetId);
      toast.success('Filter preset deleted');
    } catch (error) {
      console.error('Failed to delete filter preset:', error);
      toast.error('Failed to delete filter preset');
    }
  };

  // Resize handlers
  const [localColumnWidths, setLocalColumnWidths] = useState<Record<string, number>>(columnWidths);
  const localColumnWidthsRef = useRef(localColumnWidths);
  localColumnWidthsRef.current = localColumnWidths;

  useEffect(() => {
    setLocalColumnWidths(columnWidths);
  }, [columnWidths]);

  const startResize = (e: React.MouseEvent, columnId: string) => {
    e.preventDefault();
    const th = (e.target as HTMLElement).closest('th');
    const startWidth = th?.offsetWidth ?? 100;
    resizingRef.current = { columnId, startX: e.clientX, startWidth };
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      const { columnId, startX, startWidth } = resizingRef.current;
      const newWidth = Math.max(60, startWidth + (e.clientX - startX));
      setLocalColumnWidths((prev) => ({ ...prev, [columnId]: newWidth }));
    };
    const onMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(localColumnWidthsRef.current);
      }
      resizingRef.current = null;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [setColumnWidths]);

  // Drag handlers
  const handleDragStart = (columnId: string) => {
    dragColRef.current = columnId;
  };
  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverCol(columnId);
  };
  const handleDrop = (targetId: string) => {
    const sourceId = dragColRef.current;
    if (!sourceId || sourceId === targetId) {
      setDragOverCol(null);
      return;
    }
    const next = [...columnOrder];
    const from = next.indexOf(sourceId);
    const to = next.indexOf(targetId);
    if (from !== -1 && to !== -1) {
      next.splice(from, 1);
      next.splice(to, 0, sourceId);
      setColumnOrder(next);
    }
    setDragOverCol(null);
    dragColRef.current = null;
  };
  const handleDragEnd = () => {
    setDragOverCol(null);
    dragColRef.current = null;
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize: string) => {
    const size = parseInt(newSize, 10);
    setPageSize(size);
    setCurrentPage(1);
  };

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    saveReceiptColumnPreferences(columns);
  };

  // Map frontend column IDs to ReceiptListSort enum names for the export API.
  // Amount columns inherit the Display* variants from sortFieldMap so exported values
  // match the grid, which shows the transaction currency, not the company base currency.
  const exportColumnNameMap: Record<string, string> = useMemo(
    () => ({
      ...sortFieldMap,
      currency: 'CurrencyCode',
      customerAccountNumber: 'AccountNumber',
      allocatedAmount: 'DisplayTotalAllocated',
      createdBy: 'CreatedByName',
      createdAt: 'TransactionCreatedAt',
      modifiedBy: 'ModifiedByName',
      modifiedAt: 'TransactionModifiedAt',
    }),
    [],
  );

  // Build columns for server-side export based on visible columns
  const getExportColumns = useCallback((): ColumnInput[] => {
    return receiptVisibleColumns
      .filter((col) => col.id in exportColumnNameMap)
      .map((col) => ({
        name: exportColumnNameMap[col.id],
        displayName: col.label,
      }));
  }, [receiptVisibleColumns, exportColumnNameMap]);

  // Build columns for server-side export with all columns
  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_RECEIPT_COLUMNS.filter((col) => col.id in exportColumnNameMap).map((col) => ({
      name: exportColumnNameMap[col.id],
      displayName: col.label,
    }));
  }, [exportColumnNameMap]);

  // Reuse the same filter builder used by the GraphQL queries
  const getExportFilter = useCallback(
    (): Record<string, unknown> | undefined => buildGraphQLFilterVariable(graphqlParams),
    [graphqlParams],
  );

  // Server-side export hook for current view columns
  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: 'Receipts',
    getColumns: getExportColumns,
    getFilter: getExportFilter,
    fileName: `receipts-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Server-side export hook for all columns
  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: 'Receipts',
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    fileName: `receipts-all-columns-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Handle export to Excel (server-side)
  const handleExportToExcel = () => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  // Handle export all columns to Excel (server-side)
  const handleExportAllColumns = () => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleViewCustomer = useCallback(
    (receipt: Receipt) => {
      navigate(`/customers/${receipt.customerId}?tab=transactions`, {
        state: { customer: { name: receipt.customerName, accountNumber: receipt.customerAccountNumber } },
      });
    },
    [navigate],
  );

  const renderCustomerLink = (receipt: Receipt, value: string) => {
    if (!value) return <span className="text-muted-foreground">-</span>;
    if (!receipt.customerId) return <span className="font-medium">{value}</span>;
    return (
      <button
        type="button"
        onClick={() => handleViewCustomer(receipt)}
        className="text-primary hover:underline font-medium transition-colors"
        title={`View details for ${receipt.customerName || receipt.customerAccountNumber}`}
      >
        {value}
      </button>
    );
  };

  const getStatusBadge = (status: AllocationStatus) => {
    switch (status) {
      case 'fully_allocated':
        return <Badge className="bg-[#14b8a6] hover:bg-[#0d9488] text-white border-0">Fully Allocated</Badge>;
      case 'partially_allocated':
        return <Badge className="bg-amber-500 hover:bg-amber-600 text-white border-0">Partially Allocated</Badge>;
      case 'unallocated':
        return <Badge className="bg-red-500 hover:bg-red-600 text-white border-0">Unallocated</Badge>;
      default:
        return <Badge className="bg-muted text-muted-foreground border-0">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number, currency: string = baseCurrency) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);

  // Column header renderer
  const renderColHeader = (columnId: string) => {
    const cfg = COL_CONFIG[columnId];
    if (!cfg) return null;
    const isPinnedL = pinnedLeft.includes(columnId);
    const isPinnedR = pinnedRight.includes(columnId);
    const width = localColumnWidths[columnId];
    const isDragOver = dragOverCol === columnId;

    const label = cfg.sortField ? (
      <button
        className={cn(
          'flex items-center gap-1 hover:text-gray-900 transition-colors',
          cfg.align === 'right' && 'justify-end w-full',
        )}
        onClick={() => handleSort(cfg.sortField as SortField)}
      >
        {cfg.label}
        {renderSortIcon(cfg.sortField as SortField)}
      </button>
    ) : (
      <span className={cn(cfg.align === 'right' && 'block text-right')}>{cfg.label}</span>
    );

    return (
      <th
        key={columnId}
        draggable
        onDragStart={() => handleDragStart(columnId)}
        onDragOver={(e) => handleDragOver(e, columnId)}
        onDrop={() => handleDrop(columnId)}
        onDragEnd={handleDragEnd}
        className={cn(
          'relative h-10 px-4 bg-white font-bold text-gray-700 select-none whitespace-nowrap border-r border-gray-200 last:border-r-0 text-left',
          cfg.align === 'right' && 'text-right',
          isPinnedL && 'sticky left-0 z-20 shadow-[2px_0_4px_rgba(0,0,0,0.06)]',
          isPinnedR && 'sticky right-0 z-20 shadow-[-2px_0_4px_rgba(0,0,0,0.06)]',
          isDragOver && 'bg-blue-50 border-l-2 border-l-blue-400',
        )}
        style={width ? { width, minWidth: width } : undefined}
      >
        <div className="pr-5">{label}</div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="absolute right-5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {!isPinnedL && !isPinnedR ? (
              <>
                <DropdownMenuItem
                  onClick={() =>
                    setPinned(
                      [...pinnedLeft, columnId],
                      pinnedRight.filter((x) => x !== columnId),
                    )
                  }
                >
                  <Pin className="mr-2 h-4 w-4" /> Pin to Left
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    setPinned(
                      pinnedLeft.filter((x) => x !== columnId),
                      [...pinnedRight, columnId],
                    )
                  }
                >
                  <PinOff className="mr-2 h-4 w-4" /> Pin to Right
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem
                onClick={() =>
                  setPinned(
                    pinnedLeft.filter((x) => x !== columnId),
                    pinnedRight.filter((x) => x !== columnId),
                  )
                }
              >
                <PinOff className="mr-2 h-4 w-4" /> Unpin Column
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => resetColumnWidths()}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reset Column Widths
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => resetColumnOrder()}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reset Column Order
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div
          className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 active:bg-blue-500"
          onMouseDown={(e) => startResize(e, columnId)}
        />
      </th>
    );
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td
            colSpan={columnOrder.filter((id) => columnVisibility[id]).length}
            className="text-center py-8 text-sm text-muted-foreground"
          >
            Loading receipts...
          </td>
        </tr>
      );
    }
    if (sortedReceipts.length === 0) {
      return (
        <tr>
          <td
            colSpan={columnOrder.filter((id) => columnVisibility[id]).length}
            className="text-center py-8 text-sm text-muted-foreground"
          >
            No receipts found
          </td>
        </tr>
      );
    }
    return sortedReceipts.map((receipt, index) => (
      <tr key={receipt.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        {columnOrder
          .filter((id) => columnVisibility[id])
          .map((colId) => {
            const cell = ((): { node: React.ReactNode; className?: string } | null => {
              switch (colId) {
                case 'receiptNumber':
                  return { node: receipt.receiptNumber, className: 'font-medium' };
                case 'customerAccountNumber':
                  return { node: renderCustomerLink(receipt, receipt.customerAccountNumber) };
                case 'customerName':
                  return { node: renderCustomerLink(receipt, receipt.customerName) };
                case 'currency':
                  return { node: receipt.currency };
                case 'receiptDate':
                  return { node: format(new Date(receipt.receiptDate), 'dd/MM/yyyy') };
                case 'receivedAmount':
                  return {
                    node: formatCurrency(receipt.receivedAmount, receipt.currency),
                    className: 'text-right font-medium',
                  };
                case 'allocatedAmount':
                  return {
                    node: formatCurrency(receipt.allocatedAmount || 0, receipt.currency),
                    className: 'text-right',
                  };
                case 'unappliedAmount':
                  return {
                    node: (
                      <span
                        className={cn(
                          'font-medium',
                          receipt.unappliedAmount > 0 ? 'text-red-500' : 'text-muted-foreground',
                        )}
                      >
                        {formatCurrency(receipt.unappliedAmount, receipt.currency)}
                      </span>
                    ),
                    className: 'text-right',
                  };
                case 'allocationStatus':
                  return { node: getStatusBadge(receipt.allocationStatus) };
                case 'createdBy':
                  return { node: receipt.createdBy || '-' };
                case 'createdAt':
                  return { node: receipt.createdAt ? formatDateTime(new Date(receipt.createdAt)) : '-' };
                case 'modifiedBy':
                  return { node: receipt.modifiedBy || '-' };
                case 'modifiedAt':
                  return { node: receipt.modifiedAt ? formatDateTime(new Date(receipt.modifiedAt)) : '-' };
                case 'actions':
                  return {
                    node:
                      receipt.allocationStatus !== 'fully_allocated' ? (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/payments/allocate/${receipt.id}`);
                          }}
                          className="gap-1"
                        >
                          <ArrowRightCircle className="h-4 w-4" />
                          Allocate
                        </Button>
                      ) : null,
                    className: 'text-right',
                  };
                default:
                  return null;
              }
            })();
            if (!cell) return null;
            const isPinnedL = pinnedLeft.includes(colId);
            const isPinnedR = pinnedRight.includes(colId);
            // Only a resized column clips; until then it still grows to fit its content.
            const colWidth = localColumnWidths[colId];
            const { title, content } = clipCell(cell.node, colWidth);
            return (
              <td
                key={colId}
                title={title}
                style={colWidth ? { width: `${colWidth}px`, minWidth: `${colWidth}px` } : undefined}
                className={cn(
                  'px-4 py-3 text-sm border-r border-gray-200 last:border-r-0 whitespace-nowrap',
                  isPinnedL && 'sticky left-0 z-10 bg-inherit shadow-[2px_0_4px_rgba(0,0,0,0.06)]',
                  isPinnedR && 'sticky right-0 z-10 bg-inherit shadow-[-2px_0_4px_rgba(0,0,0,0.06)]',
                  cell.className,
                )}
              >
                {content}
              </td>
            );
          })}
      </tr>
    ));
  };

  return (
    <div className="flex flex-col h-full overflow-hidden p-4 md:p-6 pt-6 gap-4">
      {/* Page Header */}
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="text-3xl font-bold tracking-tight">Receipts</h2>
          <p className="text-muted-foreground">View and manage customer receipts and allocations</p>
        </div>
        <HelpSupportButton screenId="receipts" />
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 shrink-0">
        <ReceiptsKPICard
          title="Total Receipts"
          value={formatCurrency(kpis.totalReceived, activeCurrencyFilter)}
          subtitle={`${kpis.totalReceipts} receipts`}
          icon={getCurrencyIcon(activeCurrencyFilter)}
          colorScheme="blue"
          onClick={() => {
            clearNavigationFilterContext();
            setMetricAllocationStatus(null);
            setFilters((prev) => ({
              ...prev,
              allocationStatuses: [],
              unappliedNotZero: undefined,
            }));
          }}
          isActive={filters.allocationStatuses.length === 0 && !filters.unappliedNotZero}
          infoTooltip={
            isSingleCurrencyFiltered
              ? `Total for ${activeCurrencyFilter} receipts only. Values shown in ${activeCurrencyFilter}.`
              : `Total shown in base currency (${baseCurrency}). Includes all receipts.`
          }
        />
        <ReceiptsKPICard
          title="Fully Allocated"
          value={formatCurrency(kpis.fullyApplied, activeCurrencyFilter)}
          subtitle={`${kpis.fullyAppliedCount} receipts`}
          icon={CheckCircle2}
          colorScheme="green"
          onClick={() => handleKpiClick('fully_allocated')}
          isActive={filters.allocationStatuses.length === 1 && filters.allocationStatuses[0] === 'fully_allocated'}
          infoTooltip={
            isSingleCurrencyFiltered
              ? `Fully allocated total for ${activeCurrencyFilter} receipts only. Values shown in ${activeCurrencyFilter}.`
              : `Total shown in base currency (${baseCurrency}). Includes all receipts.`
          }
        />
        <ReceiptsKPICard
          title="Partially Allocated"
          value={formatCurrency(kpis.partiallyApplied, activeCurrencyFilter)}
          subtitle={`${kpis.partiallyAppliedCount} receipts`}
          icon={AlertCircle}
          colorScheme="amber"
          onClick={() => handleKpiClick('partially_allocated')}
          isActive={filters.allocationStatuses.length === 1 && filters.allocationStatuses[0] === 'partially_allocated'}
          infoTooltip={
            isSingleCurrencyFiltered
              ? `Partially allocated total for ${activeCurrencyFilter} receipts only. Values shown in ${activeCurrencyFilter}.`
              : `Total shown in base currency (${baseCurrency}). Includes all receipts.`
          }
          secondaryLabel="Unallocated"
          secondaryValue={formatCurrency(kpis.partiallyOutstanding, activeCurrencyFilter)}
        />
        <ReceiptsKPICard
          title="Unallocated"
          value={formatCurrency(kpis.unapplied, activeCurrencyFilter)}
          subtitle={`${kpis.unappliedCount} receipts`}
          icon={Target}
          colorScheme="red"
          onClick={() => handleKpiClick('unallocated')}
          isActive={filters.allocationStatuses.length === 1 && filters.allocationStatuses[0] === 'unallocated'}
          infoTooltip={
            isSingleCurrencyFiltered
              ? `Unallocated total for ${activeCurrencyFilter} receipts only. Values shown in ${activeCurrencyFilter}.`
              : `Total shown in base currency (${baseCurrency}). Includes all receipts.`
          }
        />
      </div>

      {/* Table Section */}
      <div className="flex-1 min-h-0 flex flex-col">
        {/* Sticky Toolbar */}
        <div className="sticky top-0 z-20 bg-white border border-b-0 rounded-t-xl px-4 py-3">
          <div className="flex items-center gap-3">
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827' }} className="whitespace-nowrap">
              {!isFilterActive && 'All '}Receipts ({totalRecords})
            </h2>

            {/* Search Bar - fills remaining space */}
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by receipt number, customer..."
                className="pl-8 h-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilterSheetOpen(true)}
              className="gap-2 whitespace-nowrap"
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>

            {/* Export and Column Config */}
            <div className="flex items-center gap-2">
              <button onClick={handleExportToExcel} className="text-gray-900 hover:text-gray-600 transition-colors p-1">
                <ArrowDownToLine className="h-5 w-5" />
              </button>
              <ColumnConfigDialog
                columns={receiptColumns}
                onApply={handleColumnConfigApply}
                onReset={resetReceiptColumnPreferences}
                trigger={
                  <button className="text-gray-900 hover:text-gray-600 transition-colors p-1">
                    <Settings className="h-5 w-5" />
                  </button>
                }
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters} className="gap-2">
                <X className="h-4 w-4" />
                Clear All Filters
              </Button>
            )}
            {hasDefaultFilter && !hasActiveFilters && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 px-3 py-1.5 rounded-md border border-blue-200">
                <Filter className="h-3.5 w-3.5 text-blue-600" />
                <span className="text-blue-700">
                  Default filter active: <span className="font-medium">Unallocated Amount &ne; 0</span>
                </span>
                <button
                  onClick={() => {
                    clearNavigationFilterContext();
                    setFilters({
                      ...filters,
                      unappliedNotZero: undefined,
                    });
                    toast.info('Default filter removed');
                  }}
                  className="ml-1 text-blue-600 hover:text-blue-800 transition-colors"
                  title="Remove default filter"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Card */}
        <Card className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-t-none border-t-0">
          <CardContent className="flex-1 min-h-0 p-0 overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 overflow-auto">
              <div className="min-w-max">
                <table className="w-full text-sm border-separate border-spacing-0">
                  <thead className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
                    <tr className="h-10 group">
                      {columnOrder.filter((id) => columnVisibility[id]).map((id) => renderColHeader(id))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">{renderTableBody()}</tbody>
                </table>
              </div>
            </div>
          </CardContent>

          {/* Pagination Footer */}
          {!isLoading && totalRecords > 0 && (
            <div className="shrink-0 flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white pr-16">
              <span className="text-sm text-gray-500">
                Page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
                <span className="font-semibold text-gray-900">{totalPages}</span>
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Rows per page:</span>
                <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {pageNumbers.map((p, i) =>
                  typeof p === 'string' ? (
                    <span key={`ellipsis-${i}`} className="px-1 text-gray-400 text-sm">
                      &hellip;
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={cn(
                        'w-8 h-8 rounded text-sm font-medium transition-colors',
                        currentPage === p ? 'bg-teal-600 text-white' : 'hover:bg-gray-100 text-gray-700',
                      )}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <ReceiptFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={filters}
        onApplyFilters={(nextFilters) => {
          const normalizedFilters = normalizeReceiptFilters(nextFilters);
          clearNavigationFilterContext();
          setFilters(normalizedFilters);
          setMetricAllocationStatus((current) =>
            current &&
            normalizedFilters.allocationStatuses.length === 1 &&
            normalizedFilters.allocationStatuses[0] === current
              ? current
              : null,
          );
          setCurrentPage(1);
        }}
        savedPresets={normalizedSavedPresets}
        activePresetId={effectiveActivePresetId}
        onApplyPreset={handleApplyPreset}
        onDeletePreset={handleDeletePreset}
        onSavePreset={(draft) => {
          pendingDraftFiltersRef.current = draft;
          setSavePresetDialogOpen(true);
        }}
        onClearAll={handleClearFilters}
      />

      <Dialog open={savePresetDialogOpen} onOpenChange={setSavePresetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Filter Preset</DialogTitle>
            <DialogDescription>Save your current filters as a preset for quick access later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="receipt-preset-name">Preset Name</Label>
              <Input
                id="receipt-preset-name"
                placeholder="e.g., Unallocated Bank Transfers"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePreset}>
              <Save className="mr-2 h-4 w-4" />
              Save Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
